<template>
  <!-- Kit ClSegmented: equal segments on a gray track, the selected one on
       a lighter thumb. Pointer 28 tall, touch 36 (the app's regular size).
       Options are { value, label }. -->
  <div class="cl-seg" :class="size ? `cl-seg--sized cl-seg--${size}` : null" role="group" :aria-label="label">
    <button
      v-for="o in options"
      :key="o.value"
      type="button"
      class="cl-seg-item"
      :class="{ 'is-on': o.value === modelValue }"
      :aria-pressed="o.value === modelValue ? 'true' : 'false'"
      @click="$emit('update:modelValue', o.value)"
    >
      {{ o.label }}
    </button>
  </div>
</template>

<script>
export default {
  name: "ClSegmented",
  props: {
    modelValue: { type: [String, Number], default: null },
    options: { type: Array, required: true },
    label: { type: String, default: null },
    // The shell's sizes (SIGNED_IN_PLAN 3.3): pointer 28; touch regular 36,
    // medium 34, small 32; labels 12 on pointer, 14 on touch. Without a
    // size it keeps the auth frame's look.
    size: {
      type: String,
      default: null,
      validator: (v) => v === null || ["regular", "medium", "small"].includes(v),
    },
  },
  emits: ["update:modelValue"],
};
</script>

<style scoped>
.cl-seg {
  --seg-h: 28px;
  --seg-track-r: 7px;
  --seg-thumb-r: 5px;
  display: flex;
  gap: 2px;
  height: var(--seg-h);
  padding: 2px;
  border-radius: var(--seg-track-r);
  background: var(--cl-control-fill);
  box-sizing: border-box;
}
@media (pointer: coarse) {
  .cl-seg {
    --seg-h: 36px;
    --seg-track-r: 10px;
    --seg-thumb-r: 8px;
  }
}
.cl-seg-item {
  flex: 1 1 0;
  min-width: 0;
  border: none;
  border-radius: var(--seg-thumb-r);
  background: transparent;
  color: var(--cl-label);
  font: var(--cl-subhead);
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background var(--cl-t-fast);
}
.cl-seg-item:hover:not(.is-on) {
  background: var(--cl-hover);
}
.cl-seg-item.is-on {
  background: var(--cl-segment-thumb);
  color: var(--cl-on-fill);
  font-weight: 600;
  cursor: default;
}
@media (pointer: coarse) {
  .cl-seg-item.is-on {
    /* The app's touch thumb: 0 2 6, scrim at .3 */
    box-shadow: 0 2px 6px color-mix(in srgb, var(--cl-scrim) 30%, transparent);
  }
}
/* Sized (the shell): density from <html data-density>, never a media
   query. */
.cl-seg.cl-seg--sized {
  --seg-h: 28px;
  --seg-track-r: 7px;
  --seg-thumb-r: 5px;
}
.cl-seg--sized .cl-seg-item {
  font: 400 12px/16px var(--cl-font);
}
.cl-seg--sized .cl-seg-item.is-on {
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}
:root[data-density="touch"] .cl-seg.cl-seg--regular {
  --seg-h: 36px;
  --seg-track-r: 10px;
  --seg-thumb-r: 8px;
}
:root[data-density="touch"] .cl-seg.cl-seg--medium {
  --seg-h: 34px;
  --seg-track-r: 9px;
  --seg-thumb-r: 7px;
}
:root[data-density="touch"] .cl-seg.cl-seg--small {
  --seg-h: 32px;
  --seg-track-r: 9px;
  --seg-thumb-r: 7px;
}
:root[data-density="touch"] .cl-seg--sized .cl-seg-item {
  font-size: 14px;
  line-height: 18px;
}
.cl-seg-item:focus-visible {
  outline: 2px solid var(--cl-accent);
  outline-offset: 1px;
}
</style>
